import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';

// project-imports
import NavItem from './NavItem';
import SIDEBAR from '../../sidebarStyles';
import { dispatch, useSelector } from 'store';
import { openDrawer } from 'store/reducers/menu';

// assets
import { ArrowDown2 } from 'iconsax-react';

// ==============================|| NAVIGATION - COLLAPSIBLE PARENT ||============================== //
// A "collapse" menu item is a parent row with no route of its own — clicking it
// toggles its `children` (plain "item" entries) open/closed underneath it. This
// is what turns a long flat list of 12 Master Data links (etc.) into one row.

const findActiveChild = (children, pathname) =>
  (children || []).some((child) => child?.url === pathname || findActiveChild(child?.children, pathname));

const NavCollapse = ({ item, level }) => {
  const { pathname } = useLocation();
  const { drawerOpen } = useSelector((state) => state.menu);

  const isChildActive = useMemo(() => findActiveChild(item.children, pathname), [item.children, pathname]);
  const [open, setOpen] = useState(isChildActive);

  // Land directly on a child route (deep link/refresh) -> reveal it. Never force-closes
  // on navigation elsewhere, so a manually-opened section doesn't jump shut on the user.
  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [isChildActive]);

  const Icon = item.icon;
  const itemIcon = Icon ? <Icon variant={isChildActive ? 'Bulk' : 'Linear'} size={drawerOpen ? 19 : 22} /> : false;

  const handleToggle = () => {
    if (!drawerOpen) {
      // Mini/icon-only rail has no room for a nested list — expand the drawer
      // itself and open the section so the children become visible.
      dispatch(openDrawer({ drawerOpen: true }));
      setOpen(true);
      return;
    }
    setOpen((prev) => !prev);
  };

  const parentButton = (
    <ListItemButton
      onClick={handleToggle}
      selected={isChildActive && !open}
      aria-expanded={open}
      sx={{
        position: 'relative',
        zIndex: 1201,
        pl: drawerOpen ? `${level * 18 + 14}px` : 1.5,
        pr: 1.5,
        py: drawerOpen ? 0.9 : 1.25,
        minHeight: drawerOpen ? 40 : 'auto',
        mx: drawerOpen ? 1.25 : 'auto',
        my: 0.2,
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'background-color 0.18s ease, color 0.18s ease',
        backgroundColor: isChildActive && !open ? SIDEBAR.activeBg : 'transparent',
        '&:hover': { backgroundColor: SIDEBAR.hoverBg },
        '&.Mui-focusVisible': {
          outline: `2px solid ${SIDEBAR.focusRing}`,
          outlineOffset: '-2px'
        },
        ...(!drawerOpen && {
          mx: 'auto',
          width: 46,
          height: 46,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '10px'
        })
      }}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            minWidth: drawerOpen ? 34 : 'unset',
            color: isChildActive ? SIDEBAR.accent : SIDEBAR.icon,
            transition: 'color 0.18s ease',
            ...(!drawerOpen && { width: '100%', display: 'flex', justifyContent: 'center' })
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}

      {drawerOpen && (
        <>
          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{
                  color: isChildActive ? SIDEBAR.textActive : SIDEBAR.text,
                  fontWeight: isChildActive ? 600 : 400,
                  fontSize: '0.875rem',
                  letterSpacing: '0.01em',
                  lineHeight: 1.45
                }}
              >
                {item.title}
              </Typography>
            }
          />
          <ArrowDown2
            size={14}
            color={isChildActive ? SIDEBAR.accent : SIDEBAR.icon}
            style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.18s ease', flexShrink: 0 }}
          />
        </>
      )}
    </ListItemButton>
  );

  return (
    <>
      {!drawerOpen ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 0.5 }}>
          <Tooltip title={item.title} placement="right" arrow>
            {parentButton}
          </Tooltip>
        </Box>
      ) : (
        parentButton
      )}

      {drawerOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ mt: 0 }}>
            {item.children?.map((child) =>
              child?.type === 'collapse' ? (
                <NavCollapse key={child.id} item={child} level={level + 1} />
              ) : (
                <NavItem key={child.id} item={child} level={level + 1} />
              )
            )}
          </List>
        </Collapse>
      )}
    </>
  );
};

NavCollapse.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavCollapse;
